import type { NamespaceCatalog } from '@/i18n/ns';

const meetingAudioInputCopy: NamespaceCatalog<
  | 'source'
  | 'hint'
  | 'bothHint'
  | 'guide'
  | 'load'
  | 'choose'
  | 'microphone'
  | 'unavailable'
  | 'lost'
> = {
  en: {
    source: 'Computer audio input',
    hint: 'Sound from a loopback or monitor input provided by your computer.',
    bothHint: 'The computer audio input and your microphone.',
    guide:
      'Choose a “Monitor of…” or loopback input carrying the call. It captures all sound on that output, without screen sharing. Choose Both to include your microphone. If no such input is listed, this browser cannot capture the call directly; use a supported browser or import a recording.',
    load: 'Choose audio inputs',
    choose: 'Select an input',
    microphone: 'Microphone',
    unavailable: 'Audio inputs could not be loaded. Allow microphone access and try again.',
    lost: 'An audio input stopped, so the recording paused.',
  },
  bg: {
    source: 'Аудио вход на компютъра',
    hint: 'Звук от loopback или мониторен вход на компютъра.',
    bothHint: 'Аудио входът на компютъра и вашият микрофон.',
    guide:
      'Изберете „Monitor of…“ или loopback вход със звука на разговора. Записва целия звук от този изход без споделяне на екрана. Изберете „И двете“ за микрофона. Ако няма такъв вход, използвайте поддържан браузър или импортирайте запис.',
    load: 'Избор на аудио входове',
    choose: 'Изберете вход',
    microphone: 'Микрофон',
    unavailable: 'Аудио входовете не могат да се заредят. Разрешете микрофона и опитайте отново.',
    lost: 'Аудио вход спря и записът е на пауза.',
  },
  hr: {
    source: 'Audio ulaz računala',
    hint: 'Zvuk s povratnog ili monitorskog ulaza računala.',
    bothHint: 'Audio ulaz računala i vaš mikrofon.',
    guide:
      'Odaberite „Monitor of…” ili povratni ulaz sa zvukom poziva. Snima sav zvuk tog izlaza bez dijeljenja zaslona. Odaberite „Oboje” za mikrofon. Ako takvog ulaza nema, koristite podržani preglednik ili uvezite snimku.',
    load: 'Odaberi audio ulaze',
    choose: 'Odaberite ulaz',
    microphone: 'Mikrofon',
    unavailable: 'Audio ulazi nisu učitani. Dopustite pristup mikrofonu i pokušajte ponovno.',
    lost: 'Audio ulaz je prestao raditi pa je snimanje pauzirano.',
  },
  cs: {
    source: 'Zvukový vstup počítače',
    hint: 'Zvuk ze zpětného nebo monitorovacího vstupu počítače.',
    bothHint: 'Zvukový vstup počítače a váš mikrofon.',
    guide:
      'Vyberte „Monitor of…“ nebo vstup se zpětným zvukem hovoru. Zachytí veškerý zvuk výstupu bez sdílení obrazovky. Pro mikrofon vyberte „Obojí“. Pokud takový vstup chybí, použijte podporovaný prohlížeč nebo importujte nahrávku.',
    load: 'Vybrat zvukové vstupy',
    choose: 'Vyberte vstup',
    microphone: 'Mikrofon',
    unavailable: 'Zvukové vstupy se nepodařilo načíst. Povolte mikrofon a zkuste to znovu.',
    lost: 'Zvukový vstup se zastavil, nahrávání je pozastaveno.',
  },
  da: {
    source: 'Computerens lydindgang',
    hint: 'Lyd fra en loopback- eller monitorindgang på computeren.',
    bothHint: 'Computerens lydindgang og din mikrofon.',
    guide:
      'Vælg en „Monitor of…“- eller loopback-indgang med opkaldets lyd. Den optager al lyd fra den udgang uden skærmdeling. Vælg „Begge“ for mikrofonen. Hvis indgangen mangler, brug en understøttet browser eller importér en optagelse.',
    load: 'Vælg lydindgange',
    choose: 'Vælg en indgang',
    microphone: 'Mikrofon',
    unavailable: 'Lydindgange kunne ikke indlæses. Tillad mikrofonadgang, og prøv igen.',
    lost: 'En lydindgang stoppede, så optagelsen er sat på pause.',
  },
  nl: {
    source: 'Audio-ingang van de computer',
    hint: 'Geluid van een loopback- of monitoringang op je computer.',
    bothHint: 'De audio-ingang van de computer en je microfoon.',
    guide:
      'Kies een ‘Monitor of…’- of loopback-ingang met het gesprek. Dit neemt al het geluid van die uitgang op zonder scherm delen. Kies ‘Beide’ voor je microfoon. Ontbreekt zo’n ingang, gebruik dan een ondersteunde browser of importeer een opname.',
    load: 'Audio-ingangen kiezen',
    choose: 'Kies een ingang',
    microphone: 'Microfoon',
    unavailable:
      'Audio-ingangen konden niet worden geladen. Sta microfoontoegang toe en probeer opnieuw.',
    lost: 'Een audio-ingang is gestopt, dus de opname is gepauzeerd.',
  },
  et: {
    source: 'Arvuti helisisend',
    hint: 'Heli arvuti loopback- või monitorisisendist.',
    bothHint: 'Arvuti helisisend ja sinu mikrofon.',
    guide:
      'Vali „Monitor of…“ või loopback-sisend, mis edastab kõne heli. See salvestab kogu väljundi heli ilma ekraani jagamata. Mikrofoni jaoks vali „Mõlemad“. Kui sobiv sisend puudub, kasuta toetatud brauserit või impordi salvestis.',
    load: 'Vali helisisendid',
    choose: 'Vali sisend',
    microphone: 'Mikrofon',
    unavailable: 'Helisisendeid ei saanud laadida. Luba mikrofon ja proovi uuesti.',
    lost: 'Helisisend peatus, salvestamine on pausil.',
  },
  fi: {
    source: 'Tietokoneen äänitulo',
    hint: 'Ääni tietokoneen loopback- tai monitoritulosta.',
    bothHint: 'Tietokoneen äänitulo ja mikrofonisi.',
    guide:
      'Valitse puhelun ääntä toistava „Monitor of…“- tai loopback-tulo. Se tallentaa kaiken lähdön äänen ilman näytön jakamista. Valitse „Molemmat“ mikrofonia varten. Jos tuloa ei ole, käytä tuettua selainta tai tuo tallenne.',
    load: 'Valitse äänitulot',
    choose: 'Valitse tulo',
    microphone: 'Mikrofoni',
    unavailable: 'Äänituloja ei voitu ladata. Salli mikrofonin käyttö ja yritä uudelleen.',
    lost: 'Äänitulo pysähtyi, joten tallennus keskeytettiin.',
  },
  fr: {
    source: 'Entrée audio de l’ordinateur',
    hint: 'Son d’une entrée de bouclage ou de monitoring de l’ordinateur.',
    bothHint: 'L’entrée audio de l’ordinateur et votre micro.',
    guide:
      'Choisissez une entrée « Monitor of… » ou de bouclage contenant l’appel. Elle capte tous les sons de cette sortie sans partage d’écran. Choisissez « Les deux » pour le micro. Si aucune entrée ne convient, utilisez un navigateur compatible ou importez un enregistrement.',
    load: 'Choisir les entrées audio',
    choose: 'Sélectionnez une entrée',
    microphone: 'Microphone',
    unavailable: 'Impossible de charger les entrées audio. Autorisez le micro et réessayez.',
    lost: 'Une entrée audio s’est arrêtée, l’enregistrement est en pause.',
  },
  de: {
    source: 'Audioeingang des Computers',
    hint: 'Ton von einem Loopback- oder Monitoreingang des Computers.',
    bothHint: 'Der Audioeingang des Computers und Ihr Mikrofon.',
    guide:
      'Wählen Sie einen Eingang „Monitor von …“ oder einen Loopback-Eingang, der den Anruf überträgt. Damit werden alle Töne dieses Ausgangs erfasst, ohne dass der Bildschirm geteilt wird. Wählen Sie „Beide“, um Ihr Mikrofon einzubeziehen. Wenn kein solcher Eingang aufgeführt ist, kann dieser Browser den Anruf nicht direkt erfassen. Verwenden Sie einen unterstützten Browser oder importieren Sie eine Aufnahme.',
    load: 'Audioeingänge auswählen',
    choose: 'Eingang auswählen',
    microphone: 'Mikrofon',
    unavailable:
      'Audioeingänge konnten nicht geladen werden. Erlauben Sie den Mikrofonzugriff und versuchen Sie es erneut.',
    lost: 'Ein Audioeingang wurde beendet, die Aufnahme ist pausiert.',
  },
  el: {
    source: 'Είσοδος ήχου υπολογιστή',
    hint: 'Ήχος από είσοδο loopback ή monitor του υπολογιστή.',
    bothHint: 'Η είσοδος ήχου του υπολογιστή και το μικρόφωνό σας.',
    guide:
      'Επιλέξτε είσοδο «Monitor of…» ή loopback που μεταφέρει την κλήση. Καταγράφει όλους τους ήχους της εξόδου χωρίς κοινή χρήση οθόνης. Επιλέξτε «Και τα δύο» για το μικρόφωνο. Αν δεν υπάρχει τέτοια είσοδος, χρησιμοποιήστε συμβατό πρόγραμμα περιήγησης ή εισαγάγετε εγγραφή.',
    load: 'Επιλογή εισόδων ήχου',
    choose: 'Επιλέξτε είσοδο',
    microphone: 'Μικρόφωνο',
    unavailable: 'Δεν φορτώθηκαν οι είσοδοι ήχου. Επιτρέψτε το μικρόφωνο και δοκιμάστε ξανά.',
    lost: 'Μια είσοδος ήχου σταμάτησε και η εγγραφή τέθηκε σε παύση.',
  },
  hu: {
    source: 'A számítógép hangbemenete',
    hint: 'Hang a számítógép loopback- vagy monitorbemenetéről.',
    bothHint: 'A számítógép hangbemenete és a mikrofonod.',
    guide:
      'Válassz a hívást tartalmazó „Monitor of…” vagy loopback-bemenetet. A kimenet összes hangját rögzíti képernyőmegosztás nélkül. A mikrofonhoz válaszd a „Mindkettő” lehetőséget. Ha nincs ilyen bemenet, használj támogatott böngészőt vagy importálj felvételt.',
    load: 'Hangbemenetek kiválasztása',
    choose: 'Válassz bemenetet',
    microphone: 'Mikrofon',
    unavailable: 'A hangbemenetek nem tölthetők be. Engedélyezd a mikrofont, és próbáld újra.',
    lost: 'Egy hangbemenet leállt, a felvétel szünetel.',
  },
  ga: {
    source: 'Ionchur fuaime an ríomhaire',
    hint: 'Fuaim ó ionchur loopback nó monatóireachta ar an ríomhaire.',
    bothHint: 'Ionchur fuaime an ríomhaire agus do mhicreafón.',
    guide:
      'Roghnaigh ionchur „Monitor of…“ nó loopback a iompraíonn an glao. Gabhann sé gach fuaim ón aschur sin gan comhroinnt scáileáin. Roghnaigh „An dá cheann“ don mhicreafón. Mura bhfuil ionchur den sórt sin ann, úsáid brabhsálaí tacaithe nó iompórtáil taifeadadh.',
    load: 'Roghnaigh ionchuir fuaime',
    choose: 'Roghnaigh ionchur',
    microphone: 'Micreafón',
    unavailable:
      'Níorbh fhéidir na hionchuir fuaime a luchtú. Ceadaigh an micreafón agus bain triail eile as.',
    lost: 'Stop ionchur fuaime, mar sin cuireadh an taifeadadh ar sos.',
  },
  it: {
    source: 'Ingresso audio del computer',
    hint: 'Audio da un ingresso loopback o monitor del computer.',
    bothHint: 'L’ingresso audio del computer e il tuo microfono.',
    guide:
      'Scegli un ingresso “Monitor of…” o loopback che contiene la chiamata. Acquisisce tutti i suoni di quell’uscita senza condividere lo schermo. Scegli “Entrambi” per il microfono. Se l’ingresso non è disponibile, usa un browser supportato o importa una registrazione.',
    load: 'Scegli ingressi audio',
    choose: 'Seleziona un ingresso',
    microphone: 'Microfono',
    unavailable:
      'Impossibile caricare gli ingressi audio. Consenti l’accesso al microfono e riprova.',
    lost: 'Un ingresso audio si è interrotto, la registrazione è in pausa.',
  },
  lv: {
    source: 'Datora audio ievade',
    hint: 'Skaņa no datora loopback vai monitora ievades.',
    bothHint: 'Datora audio ievade un jūsu mikrofons.',
    guide:
      'Izvēlieties „Monitor of…” vai loopback ievadi ar zvana skaņu. Tā ieraksta visu šīs izvades skaņu bez ekrāna kopīgošanas. Mikrofonam izvēlieties „Abi”. Ja šādas ievades nav, izmantojiet atbalstītu pārlūku vai importējiet ierakstu.',
    load: 'Izvēlēties audio ievades',
    choose: 'Izvēlieties ievadi',
    microphone: 'Mikrofons',
    unavailable: 'Audio ievades nevarēja ielādēt. Atļaujiet mikrofonu un mēģiniet vēlreiz.',
    lost: 'Audio ievade apstājās, tāpēc ieraksts ir pauzēts.',
  },
  lt: {
    source: 'Kompiuterio garso įvestis',
    hint: 'Garsas iš kompiuterio loopback arba monitoriaus įvesties.',
    bothHint: 'Kompiuterio garso įvestis ir jūsų mikrofonas.',
    guide:
      'Pasirinkite skambutį perduodančią „Monitor of…“ arba loopback įvestį. Ji įrašo visus išvesties garsus be ekrano bendrinimo. Mikrofonui pasirinkite „Abu“. Jei tokios įvesties nėra, naudokite palaikomą naršyklę arba importuokite įrašą.',
    load: 'Pasirinkti garso įvestis',
    choose: 'Pasirinkite įvestį',
    microphone: 'Mikrofonas',
    unavailable:
      'Nepavyko įkelti garso įvesčių. Leiskite naudoti mikrofoną ir bandykite dar kartą.',
    lost: 'Garso įvestis sustojo, todėl įrašymas pristabdytas.',
  },
  mt: {
    source: 'Input tal-awdjo tal-kompjuter',
    hint: 'Ħoss minn input loopback jew monitor tal-kompjuter.',
    bothHint: 'L-input tal-awdjo tal-kompjuter u l-mikrofonu tiegħek.',
    guide:
      'Agħżel input “Monitor of…” jew loopback li jġorr is-sejħa. Jaqbad il-ħoss kollu minn dak l-output mingħajr qsim tal-iskrin. Agħżel “It-tnejn” għall-mikrofonu. Jekk ma hemmx input bħal dan, uża browser appoġġjat jew importa reġistrazzjoni.',
    load: 'Agħżel inputs tal-awdjo',
    choose: 'Agħżel input',
    microphone: 'Mikrofonu',
    unavailable:
      'L-inputs tal-awdjo ma setgħux jitgħabbew. Ħalli aċċess għall-mikrofonu u erġa’ pprova.',
    lost: 'Input tal-awdjo waqaf, għalhekk ir-reġistrazzjoni ġiet sospiża.',
  },
  pl: {
    source: 'Wejście audio komputera',
    hint: 'Dźwięk z wejścia loopback lub monitora komputera.',
    bothHint: 'Wejście audio komputera i Twój mikrofon.',
    guide:
      'Wybierz wejście „Monitor of…” lub loopback z dźwiękiem rozmowy. Nagrywa wszystkie dźwięki tego wyjścia bez udostępniania ekranu. Wybierz „Oba” dla mikrofonu. Jeśli nie ma takiego wejścia, użyj obsługiwanej przeglądarki lub zaimportuj nagranie.',
    load: 'Wybierz wejścia audio',
    choose: 'Wybierz wejście',
    microphone: 'Mikrofon',
    unavailable: 'Nie można wczytać wejść audio. Zezwól na mikrofon i spróbuj ponownie.',
    lost: 'Wejście audio przestało działać, więc nagrywanie wstrzymano.',
  },
  pt: {
    source: 'Entrada de áudio do computador',
    hint: 'Som de uma entrada loopback ou de monitorização do computador.',
    bothHint: 'A entrada de áudio do computador e o seu microfone.',
    guide:
      'Escolha uma entrada «Monitor of…» ou loopback com o som da chamada. Capta todo o som dessa saída sem partilhar o ecrã. Escolha «Ambos» para o microfone. Se não existir essa entrada, use um navegador compatível ou importe uma gravação.',
    load: 'Escolher entradas de áudio',
    choose: 'Selecione uma entrada',
    microphone: 'Microfone',
    unavailable:
      'Não foi possível carregar as entradas de áudio. Permita o microfone e tente novamente.',
    lost: 'Uma entrada de áudio parou, pelo que a gravação foi pausada.',
  },
  ro: {
    source: 'Intrare audio a computerului',
    hint: 'Sunet de la o intrare loopback sau monitor a computerului.',
    bothHint: 'Intrarea audio a computerului și microfonul tău.',
    guide:
      'Alege o intrare „Monitor of…” sau loopback care transmite apelul. Captează tot sunetul acelei ieșiri fără partajarea ecranului. Alege „Ambele” pentru microfon. Dacă nu există o astfel de intrare, folosește un browser compatibil sau importă o înregistrare.',
    load: 'Alege intrările audio',
    choose: 'Selectează o intrare',
    microphone: 'Microfon',
    unavailable:
      'Intrările audio nu au putut fi încărcate. Permite accesul la microfon și încearcă din nou.',
    lost: 'O intrare audio s-a oprit, așa că înregistrarea este în pauză.',
  },
  sk: {
    source: 'Zvukový vstup počítača',
    hint: 'Zvuk zo spätného alebo monitorovacieho vstupu počítača.',
    bothHint: 'Zvukový vstup počítača a váš mikrofón.',
    guide:
      'Vyberte vstup „Monitor of…“ alebo loopback so zvukom hovoru. Zachytí všetok zvuk výstupu bez zdieľania obrazovky. Pre mikrofón vyberte „Oboje“. Ak taký vstup chýba, použite podporovaný prehliadač alebo importujte nahrávku.',
    load: 'Vybrať zvukové vstupy',
    choose: 'Vyberte vstup',
    microphone: 'Mikrofón',
    unavailable: 'Zvukové vstupy sa nepodarilo načítať. Povoľte mikrofón a skúste znova.',
    lost: 'Zvukový vstup sa zastavil, nahrávanie je pozastavené.',
  },
  sl: {
    source: 'Zvočni vhod računalnika',
    hint: 'Zvok iz povratnega ali monitorskega vhoda računalnika.',
    bothHint: 'Zvočni vhod računalnika in vaš mikrofon.',
    guide:
      'Izberite vhod »Monitor of…« ali loopback z zvokom klica. Zajame ves zvok tega izhoda brez deljenja zaslona. Za mikrofon izberite »Oboje«. Če takega vhoda ni, uporabite podprt brskalnik ali uvozite posnetek.',
    load: 'Izberi zvočne vhode',
    choose: 'Izberite vhod',
    microphone: 'Mikrofon',
    unavailable: 'Zvočnih vhodov ni bilo mogoče naložiti. Dovolite mikrofon in poskusite znova.',
    lost: 'Zvočni vhod se je ustavil, zato je snemanje začasno ustavljeno.',
  },
  es: {
    source: 'Entrada de audio del ordenador',
    hint: 'Sonido de una entrada loopback o de monitor del ordenador.',
    bothHint: 'La entrada de audio del ordenador y tu micrófono.',
    guide:
      'Elige una entrada «Monitor of…» o loopback con el audio de la llamada. Captura todo el sonido de esa salida sin compartir pantalla. Elige «Ambos» para el micrófono. Si no hay una entrada así, usa un navegador compatible o importa una grabación.',
    load: 'Elegir entradas de audio',
    choose: 'Selecciona una entrada',
    microphone: 'Micrófono',
    unavailable:
      'No se pudieron cargar las entradas de audio. Permite el micrófono e inténtalo de nuevo.',
    lost: 'Una entrada de audio se detuvo y la grabación está en pausa.',
  },
  sv: {
    source: 'Datorljud via ljudingång',
    hint: 'Ljud från en loopback- eller monitoringång på datorn.',
    bothHint: 'Datorns ljudingång och din mikrofon.',
    guide:
      'Välj en ”Monitor of…”- eller loopback-ingång med samtalets ljud. Allt ljud från den utgången spelas in, utan skärmdelning. Välj ”Båda” för att få med mikrofonen. Saknas en sådan ingång behöver du en webbläsare med stöd eller importera en inspelning.',
    load: 'Välj ljudingångar',
    choose: 'Välj en ingång',
    microphone: 'Mikrofon',
    unavailable: 'Ljudingångarna kunde inte läsas in. Tillåt mikrofonen och försök igen.',
    lost: 'En ljudingång stoppades, så inspelningen pausades.',
  },
};

export default meetingAudioInputCopy;
